import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    createFilterOptions,
} from '@mui/material';
import { AdminPageContainer, StaticCard } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import { getUserAccessFlags, setUserApiAccess } from '../../redux/slices/users';
import { setUserCalendarOptOut } from '../../redux/ducks/calendar';
import { getClients } from '../../redux/ducks/client';
import { showAlert } from '../../redux/slices/alerts';
import { useHasPermission } from '../../utils/permissionUtils';
import { statusSeverity } from '../../utils/statusColors';

const FilterCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const FilterHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FilterRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
}));

const FilterField = styled(Box)({
    flex: '1 1 220px',
    minWidth: 0,
});

const RoleCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

// Switch and chip colours come from the shared status table: granted/live reads as Active,
// a deliberate suspension as Paused, a denied or dead state as Inactive.
const ON_SEVERITY = statusSeverity('active', 'Active');
const SUSPENDED_SEVERITY = statusSeverity('active', 'Paused');

const BOOLEAN_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'true', label: 'On' },
    { value: 'false', label: 'Off' },
];

const filterClients = createFilterOptions({ limit: 50, stringify: (client) => client?.name || '' });

const AdminUserAccess = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canManageCalendar = hasPermission('manageCalendar');
    const canAdminUsers = hasPermission('adminUsers');

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);
    const [apiAccessFilter, setApiAccessFilter] = useState('');
    const [calendarFilter, setCalendarFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [inFlight, setInFlight] = useState(new Set());

    const lastPage = total > 0 ? Math.ceil(total / limit) - 1 : 0;
    const effectivePage = Math.min(page, lastPage);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(0);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const params = useMemo(() => ({
        search: debouncedSearch || undefined,
        client: selectedClient?._id || undefined,
        apiAccess: apiAccessFilter || undefined,
        calendarOptOut: calendarFilter || undefined,
        active: activeFilter || undefined,
        start: effectivePage * limit,
        limit,
    }), [debouncedSearch, selectedClient, apiAccessFilter, calendarFilter, activeFilter, effectivePage, limit]);

    const load = useCallback(() => {
        setLoading(true);
        dispatch(getUserAccessFlags({
            params,
            callback: (data) => {
                setLoading(false);
                setRows(Array.isArray(data?.items) ? data.items : []);
                setTotal(data?.total || 0);
            },
        }));
    }, [dispatch, params]);

    useEffect(() => {
        if (canManageCalendar || canAdminUsers) load();
        else setLoading(false);
    }, [canManageCalendar, canAdminUsers, load]);

    useEffect(() => {
        if (!canManageCalendar && !canAdminUsers) return;
        // limit 0 = every client, as the other client pickers do; a capped page would hide options.
        dispatch(getClients(0, 0, (data) => {
            setClients(Array.isArray(data) ? data : data?.clients || []);
            setClientsLoading(false);
        }, { active: true }));
    }, [dispatch, canManageCalendar, canAdminUsers]);

    useEffect(() => {
        if (page !== effectivePage) setPage(effectivePage);
    }, [page, effectivePage]);

    const markInFlight = (key, busy) => setInFlight((prev) => {
        const next = new Set(prev);
        if (busy) next.add(key); else next.delete(key);
        return next;
    });

    // Flip locally first, revert on failure — same optimistic pattern the employees table used.
    const patchRow = (userId, patch) => setRows((prev) => prev.map((row) => (row._id === userId ? { ...row, ...patch } : row)));

    const toggleCalendar = (row, value) => {
        const key = `cal:${row._id}`;
        if (inFlight.has(key)) return;
        markInFlight(key, true);
        patchRow(row._id, { calendarOptOut: value });
        dispatch(setUserCalendarOptOut(row._id, value, (resp) => {
            markInFlight(key, false);
            if (resp?.ok) {
                dispatch(showAlert({ severity: 'success', message: value ? `${row.fullName} is now excluded from calendar sync` : `${row.fullName} is now included in calendar sync` }));
            } else {
                patchRow(row._id, { calendarOptOut: !value });
            }
        }));
    };

    const toggleApiAccess = (row, value) => {
        const key = `api:${row._id}`;
        if (inFlight.has(key)) return;
        markInFlight(key, true);
        patchRow(row._id, { apiAccess: value });
        dispatch(setUserApiAccess({
            userId: row._id,
            apiAccess: value,
            callback: (resp) => {
                markInFlight(key, false);
                if (resp && typeof resp.apiAccess === 'boolean') {
                    patchRow(row._id, { apiAccess: resp.apiAccess });
                    dispatch(showAlert({ severity: 'success', message: value ? `${row.fullName} can now create API keys` : `${row.fullName} can no longer create API keys, and their existing keys have stopped working` }));
                } else {
                    patchRow(row._id, { apiAccess: !value });
                }
            },
        }));
    };

    if (!canManageCalendar && !canAdminUsers) {
        return (
            <AdminPageContainer>
                <PageHeader title="User Access" />
                <Alert severity="warning">You need the "manage calendar" or "admin users" permission to change how people sign in.</Alert>
            </AdminPageContainer>
        );
    }

    const colCount = 2 + (canManageCalendar ? 1 : 0) + (canAdminUsers ? 1 : 0);

    return (
        <AdminPageContainer>
            <PageHeader
                title="User Access"
                subtitle="Per-person switches for calendar sync and API access. Everything else about a person is edited on their profile."
            />

            <FilterCard>
                <FilterHelper variant="body2" color="text.secondary">
                    Find a person with any combination of the filters, then use the switches to change what they can do. Changes take effect immediately.
                </FilterHelper>
                <FilterRow>
                    <FilterField>
                        <TextField
                            fullWidth
                            label="Find a person"
                            placeholder="Name or email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            helperText="Leave empty to list everyone"
                        />
                    </FilterField>
                    <FilterField>
                        <Autocomplete
                            options={clients}
                            loading={clientsLoading}
                            value={selectedClient}
                            onChange={(_, client) => {
                                setSelectedClient(client);
                                setPage(0);
                            }}
                            getOptionLabel={(client) => client?.name || ''}
                            filterOptions={filterClients}
                            autoHighlight
                            isOptionEqualToValue={(option, selected) => option._id === selected._id}
                            renderOption={(props, option) => <li {...props} key={option._id}>{option.name}</li>}
                            renderInput={(params) => <TextField {...params} label="Client" placeholder="Every client" helperText="Who the person belongs to" />}
                        />
                    </FilterField>
                    <FilterField>
                        <TextField
                            select
                            fullWidth
                            label="Account state"
                            value={activeFilter}
                            onChange={(e) => {
                                setActiveFilter(e.target.value);
                                setPage(0);
                            }}
                            helperText="Active or inactive people"
                        >
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                        </TextField>
                    </FilterField>
                    {canManageCalendar && (
                        <FilterField>
                            <TextField
                                select
                                fullWidth
                                label="Calendar opt-out"
                                value={calendarFilter}
                                onChange={(e) => {
                                    setCalendarFilter(e.target.value);
                                    setPage(0);
                                }}
                                helperText="On means excluded from sync"
                            >
                                {BOOLEAN_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                        </FilterField>
                    )}
                    {canAdminUsers && (
                        <FilterField>
                            <TextField
                                select
                                fullWidth
                                label="API access"
                                value={apiAccessFilter}
                                onChange={(e) => {
                                    setApiAccessFilter(e.target.value);
                                    setPage(0);
                                }}
                                helperText="On means they can hold keys"
                            >
                                {BOOLEAN_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                        </FilterField>
                    )}
                </FilterRow>
            </FilterCard>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Person</strong></TableCell>
                            <TableCell><strong>Role</strong></TableCell>
                            {canManageCalendar && <TableCell align="center"><strong>Calendar opt-out</strong></TableCell>}
                            {canAdminUsers && <TableCell align="center"><strong>API access</strong></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={colCount}>
                                    <LoadingBox><CircularProgress /></LoadingBox>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={colCount} align="center">
                                    <Typography variant="body2" color="text.secondary">No one matches these filters.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.map((row) => (
                            <TableRow key={row._id} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">{row.fullName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                                </TableCell>
                                <TableCell>
                                    <RoleCell>
                                        <Typography variant="caption">{row.role?.name || '—'}</Typography>
                                        {!row.active && (
                                            <Chip size="small" variant="outlined" color={statusSeverity('active', 'Inactive')} label="Inactive" />
                                        )}
                                    </RoleCell>
                                </TableCell>
                                {canManageCalendar && (
                                    <TableCell align="center">
                                        <Tooltip title={row.calendarOptOut ? 'Excluded from calendar sync — switch off to include them' : 'Included in calendar sync — switch on to exclude them'}>
                                            <Switch
                                                size="small"
                                                color={SUSPENDED_SEVERITY}
                                                checked={!!row.calendarOptOut}
                                                disabled={inFlight.has(`cal:${row._id}`)}
                                                onChange={(e) => toggleCalendar(row, e.target.checked)}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                )}
                                {canAdminUsers && (
                                    <TableCell align="center">
                                        <Tooltip title={row.apiAccess ? 'Can create API keys for outside tools — switch off to stop their keys working' : 'Cannot create or use API keys — switch on to allow it'}>
                                            <Switch
                                                size="small"
                                                color={ON_SEVERITY}
                                                checked={!!row.apiAccess}
                                                disabled={inFlight.has(`api:${row._id}`)}
                                                onChange={(e) => toggleApiAccess(row, e.target.checked)}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {total > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={effectivePage}
                    onPageChange={(e, next) => setPage(next)}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[10, 25, 50]}
                    onRowsPerPageChange={(e) => {
                        setLimit(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </AdminPageContainer>
    );
};

export default AdminUserAccess;
